import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const HOVER_COMMIT_DELAY_MS = 110;
const LEAVE_GRACE_MS = 90;

/**
 * A custom element that manages the desktop header menu.
 * Rapid pointer changes are handled as one deterministic state transition so
 * stale timers and observers cannot close or resize a newly activated submenu.
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {MutationObserver | null} */
  #submenuMutationObserver = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #observerCleanupTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #deactivateTimer;
  /** @type {number | undefined} */
  #measurementFrame;

  #state = { activeItem: null };

  connectedCallback() {
    super.connectedCallback();
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerenter', this.#cancelScheduledDeactivate);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerenter', this.#cancelScheduledDeactivate);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#clearTransientWork();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    const submenu = findSubmenu(this.#state.activeItem) || this.overflowMenu;
    if (this.#state.activeItem && submenu) this.#queueHeightSync(submenu);
  }, 100);

  #overflowSubmenuListener = () => this.#scheduleDeactivate();

  #cancelScheduledDeactivate = () => {
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
  };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /** @param {PointerEvent | FocusEvent} event */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    this.#cancelScheduledDeactivate();

    const item = findMenuItem(event.target);
    if (!item) return;

    const isDefaultSlot = event.target.slot === '';
    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);
    if (!hasSubmenu && !isDefaultSlot) submenu = this.overflowMenu;

    if (item === this.#state.activeItem) {
      if (submenu) this.#queueHeightSync(submenu, isDefaultSlot, hasSubmenu);
      return;
    }

    const previousItem = this.#state.activeItem;
    const previousSubmenu = findSubmenu(previousItem);

    this.#clearObserverWork();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    if (previousItem) previousItem.ariaExpanded = 'false';
    if (previousSubmenu && previousSubmenu !== submenu) delete previousSubmenu.dataset.active;

    this.#state.activeItem = item;
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    if (submenu) {
      submenu.dataset.active = '';
      this.#armHoverEvent(event, item);
      this.#watchSubmenu(submenu, isDefaultSlot, hasSubmenu);
      this.#queueHeightSync(submenu, isDefaultSlot, hasSubmenu);
    } else {
      this.#applyHeight(0);
    }

    this.style.setProperty('--submenu-opacity', submenu ? '1' : '0');
  };

  /** @param {PointerEvent | FocusEvent} event */
  deactivate(event) {
    const relatedTarget = event.relatedTarget;
    const activeSubmenu = findSubmenu(this.#state.activeItem);

    if (relatedTarget instanceof Node) {
      const movingWithinHeaderMenu = this.contains(relatedTarget);
      const movingIntoActiveSubmenu = activeSubmenu?.contains(relatedTarget) ?? false;
      const movingIntoOverflow =
        this.overflowMenu?.contains(relatedTarget) ||
        relatedTarget.parentElement?.matches?.('[slot="overflow"]');

      if (movingWithinHeaderMenu || movingIntoActiveSubmenu || movingIntoOverflow) {
        this.#cancelScheduledDeactivate();
        return;
      }
    }

    this.#scheduleDeactivate();
  }

  #scheduleDeactivate() {
    this.#cancelScheduledDeactivate();
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      const submenu = findSubmenu(this.#state.activeItem);
      const pointerStillInside =
        this.matches(':hover') ||
        submenu?.matches(':hover') ||
        this.overflowListHovered ||
        this.overflowMenu?.matches(':hover');

      if (!pointerStillInside) this.#deactivate();
    }, LEAVE_GRACE_MS);
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    const submenu = findSubmenu(item);
    this.#clearTransientWork();

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (submenu) delete submenu.dataset.active;

    this.dataset.overflowExpanded = 'false';
    this.style.setProperty('--submenu-opacity', '0');
    this.#applyHeight(0);
  };

  /** @param {PointerEvent | FocusEvent} event @param {HTMLElement} item */
  #armHoverEvent(event, item) {
    if (event instanceof FocusEvent) {
      this.dispatchEvent(new MegaMenuHoverEvent());
      return;
    }

    this.#hoverDispatchTimer = setTimeout(() => {
      this.#hoverDispatchTimer = undefined;
      if (this.#state.activeItem === item) this.dispatchEvent(new MegaMenuHoverEvent());
    }, HOVER_COMMIT_DELAY_MS);
  }

  /** @param {HTMLElement} submenu @param {boolean} isDefaultSlot @param {boolean} hasSubmenu */
  #watchSubmenu(submenu, isDefaultSlot, hasSubmenu) {
    this.#submenuMutationObserver = new MutationObserver(() => {
      if (this.#state.activeItem && submenu.dataset.active !== undefined) {
        this.#queueHeightSync(submenu, isDefaultSlot, hasSubmenu);
      }
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true, attributes: true });

    this.#observerCleanupTimer = setTimeout(() => this.#clearObserverWork(), 700);
  }

  /** @param {HTMLElement} submenu @param {boolean} [isDefaultSlot=true] @param {boolean} [hasSubmenu=true] */
  #queueHeightSync(submenu, isDefaultSlot = true, hasSubmenu = true) {
    if (this.#measurementFrame !== undefined) cancelAnimationFrame(this.#measurementFrame);
    this.#measurementFrame = requestAnimationFrame(() => {
      this.#measurementFrame = requestAnimationFrame(() => {
        this.#measurementFrame = undefined;
        if (!this.#state.activeItem || submenu.dataset.active === undefined) return;

        let finalHeight = submenu.offsetHeight || 0;
        if (!isDefaultSlot) {
          const overflowListHeight = this.#getOverflowListLinksHeight();
          finalHeight = hasSubmenu
            ? Math.max(this.overflowMenu?.offsetHeight || 0, overflowListHeight)
            : overflowListHeight;
        }
        this.#applyHeight(finalHeight);
      });
    });
  }

  /** @param {number} submenuHeight */
  #applyHeight(submenuHeight) {
    if (!this.headerComponent) return;
    const headerVisibleHeight = submenuHeight > 0 ? this.#getHeaderVisibleHeight() : 0;
    this.headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.headerComponent.style.setProperty(
      '--full-open-header-height',
      `${submenuHeight > 0 ? submenuHeight + headerVisibleHeight : 0}px`
    );
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    const submenus = slottedMenuLinks
      .map((link) => link.querySelector('[ref="submenu[]"]'))
      .filter((submenu) => submenu instanceof HTMLElement);

    submenus.forEach((submenu) => submenu.style.setProperty('display', 'none'));
    const height = this.overflowMenu?.offsetHeight || 0;
    submenus.forEach((submenu) => submenu.style.removeProperty('display'));
    return height;
  }

  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;
    const overlapsBottomRow = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    return overlapsBottomRow && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  #preloadImages = () => {
    this.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
  };

  #clearObserverWork() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
    clearTimeout(this.#observerCleanupTimer);
    this.#observerCleanupTimer = undefined;
    if (this.#measurementFrame !== undefined) cancelAnimationFrame(this.#measurementFrame);
    this.#measurementFrame = undefined;
  }

  #clearTransientWork() {
    this.#clearObserverWork();
    clearTimeout(this.#hoverDispatchTimer);
    clearTimeout(this.#deactivateTimer);
    this.#hoverDispatchTimer = undefined;
    this.#deactivateTimer = undefined;
  }
}

if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);

/** @param {Element | null | undefined} element @returns {HTMLElement | null} */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  const directItem = element.querySelector('[ref="menuitem"]');
  if (directItem instanceof HTMLElement) return directItem;
  const listItem = element.closest('.menu-list__list-item');
  const nestedItem = listItem?.querySelector('[ref="menuitem"]');
  return nestedItem instanceof HTMLElement ? nestedItem : null;
}

/** @param {Element | null | undefined} element @returns {HTMLElement | null} */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
